import { trackConversion } from "@/lib/analytics/gtag";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import { PageShell, PageHero } from "@/components/PageShell";
import { SITE } from "@/lib/site";
import { CancellationPolicy, CANCELLATION_POLICY_VERSION } from "@/components/CancellationPolicy";
import { CheckCircle2, Calendar, Clock, MapPin, Shirt, MessageCircle, CreditCard, ShieldCheck, Loader2 } from "lucide-react";

export const Route = createFileRoute("/booking")({
  component: Booking,
  head: () => ({
    meta: [
      { title: "Book a Physio in Centurion | Same Day Appointments" },
      { name: "description", content: "Book a physiotherapy appointment in Centurion online. Same day bookings, in clinic or home visits, medical aid accepted. Secure your slot in minutes." },
      { property: "og:title", content: "Book a Physio in Centurion | Same Day Appointments" },
      { property: "og:description", content: "Book physiotherapy online in Centurion. Choose your service, date and time. Medical aid accepted." },
      { property: "og:url", content: "https://nsphysiotherapy.co.za/booking" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://nsphysiotherapy.co.za/booking" }],
  }),
});

import { BOOKING_TYPES, CASH_PRICE_NOTE } from "@/lib/booking-types";
const TYPES = BOOKING_TYPES;

const LOCATIONS = ["Centurion Clinic", "Home Visit"] as const;
type Location = typeof LOCATIONS[number];

function slotsForDate(d: Date | null): string[] {
  if (!d) return [];
  const startHour = d.getDay() === 0 ? 10 : 8; // Sunday 10am, other days 8am
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const minHour = isToday ? Math.max(startHour, now.getHours() + 1) : startHour;
  const slots: string[] = [];
  for (let h = minHour; h <= 19; h++) slots.push(`${String(h).padStart(2, "0")}:00`);
  return slots;
}

function addDays(base: Date, n: number) { const d = new Date(base); d.setDate(d.getDate() + n); return d; }
const fmt = (d: Date) => d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });

function Booking() {
  const [step, setStep] = useState(1);
  const [type, setType] = useState(TYPES[0]);
  const [location, setLocation] = useState<Location>("Centurion Clinic");
  const [address, setAddress] = useState("");
  const [date, setDate] = useState<Date | null>(null);
  const [slot, setSlot] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [condition, setCondition] = useState("");
  const [payment, setPayment] = useState<"Cash" | "Medical Aid" | "Card">("Card");
  const [paidOnline, setPaidOnline] = useState(false);
  const [returnState, setReturnState] = useState<"checking" | "failed" | null>(null);
  const [medicalAid, setMedicalAid] = useState("");
  const [medicalAidNumber, setMedicalAidNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [dob, setDob] = useState("");
  const [idType, setIdType] = useState<"SA ID" | "Passport">("SA ID");
  const [idNumber, setIdNumber] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [bookedRanges, setBookedRanges] = useState<Array<{ start: number; end: number }>>([]);
  const [calendarDown, setCalendarDown] = useState(false);
  const [provisionalResult, setProvisionalResult] = useState(false);

  useEffect(() => {
    if (calendarDown && payment === "Card") setPayment("Cash");
  }, [calendarDown, payment]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch("/api/public/bookings", { headers: { Accept: "application/json" } });
        if (!res.ok) return;
        const body = (await res.json()) as { bookings: Array<{ appointment_at: string; duration_minutes: number }>; calendarUnavailable?: boolean };
        if (cancelled) return;
        // Diary unreachable: booking still works, but only as a provisional
        // booking without card payment, confirmed manually by the practice.
        setCalendarDown(Boolean(body.calendarUnavailable));
        setBookedRanges(
          (body.bookings ?? []).map((b) => {
            const s = new Date(b.appointment_at).getTime();
            return { start: s, end: s + (b.duration_minutes ?? 0) * 60 * 1000 };
          }),
        );
      } catch {
        /* non-fatal, submit will still block conflicts */
      }
    }
    load();
    return () => { cancelled = true; };
  }, [confirmed]);

  // Returning from the card payment gateway: verify the payment before confirming.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    const token = params.get("t");
    if (!ref || !token) return;
    const outcome = params.get("status");
    let cancelled = false;
    setReturnState("checking");

    async function poll() {
      for (let attempt = 0; attempt < (outcome === "success" ? 10 : 1); attempt++) {
        if (cancelled) return;
        try {
          const res = await fetch(`/api/public/booking-status?ref=${encodeURIComponent(ref!)}&t=${encodeURIComponent(token!)}`);
          if (res.ok) {
            const b = (await res.json()) as {
              service: string; duration_minutes: number; price: string; location: string; address: string | null;
              appointment_at: string; client_name: string; payment_status: string;
            };
            if (b.payment_status === "paid") {
              if (cancelled) return;
              const start = new Date(b.appointment_at);
              const matched = TYPES.find((t) => t.name === b.service);
              if (matched) setType(matched);
              setLocation(b.location as Location);
              setAddress(b.address ?? "");
              setDate(start);
              setSlot(`${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`);
              setName(b.client_name);
              setPayment("Card");
              setPaidOnline(true);
              trackConversion("purchase", "purchase", {
                value: Number(b.price) || undefined,
                currency: "ZAR",
                transaction_id: ref!,
                service_booked: b.service,
                clinic_location: b.location,
                payment_method: "Card",
              });
              trackConversion("booking", "appointment_booked", {
                service_booked: b.service,
                clinic_location: b.location,
                value: Number(b.price) || undefined,
                currency: "ZAR",
                payment_method: "Card",
              });
              setConfirmed(true);
              setReturnState(null);
              window.history.replaceState({}, "", "/booking");
              return;
            }
          }
        } catch { /* retry */ }
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (cancelled) return;
      setReturnState("failed");
      setSubmitError(
        outcome === "cancelled"
          ? "Your card payment was cancelled, so the appointment was not booked. You can try again below."
          : outcome === "success"
          ? "Your payment went through and your appointment slot is being held. We are waiting on the final confirmation from the payment gateway, which is checked again automatically. Please do not pay again. Your confirmation email will arrive shortly, or WhatsApp 064 873 3990 and we will confirm for you."
          : "We could not confirm your card payment, so the appointment was not booked. Please try again or WhatsApp 064 873 3990.",
      );
      window.history.replaceState({}, "", "/booking");
    }
    poll();
    return () => { cancelled = true; };
  }, []);



  function isSlotTaken(d: Date, hhmm: string, durationMin: number) {
    const [h, m] = hhmm.split(":").map(Number);
    const start = new Date(d);
    start.setHours(h, m, 0, 0);
    const startMs = start.getTime();
    const endMs = startMs + durationMin * 60 * 1000;
    return bookedRanges.some((r) => r.start < endMs && r.end > startMs);
  }


  // When Home Visit type is chosen, force location to Home
  function selectType(t: typeof TYPES[number]) {
    setType(t);
    if (t.id === "home") setLocation("Home Visit");
  }

  const days = useMemo(() => {
    const today = new Date();
    return Array.from({ length: 14 }, (_, i) => addDays(today, i));
  }, []);

  function next() { setStep((s) => Math.min(4, s + 1)); }
  function prev() { setStep((s) => Math.max(1, s - 1)); }

  const canContinue =
    (step === 1) ||
    (step === 2 && (location === "Centurion Clinic" || (location === "Home Visit" && address.trim().length > 3))) ||
    (step === 3 && !!date && !!slot);

  function calendarLink() {
    if (!date || !slot) return "#";
    const [h, m] = slot.split(":").map(Number);
    const start = new Date(date); start.setHours(h, m, 0, 0);
    const end = new Date(start); end.setMinutes(end.getMinutes() + type.duration);
    const iso = (d: Date) => d.toISOString().replace(/[-:]|\.\d{3}/g, "");
    const url = new URL("https://calendar.google.com/calendar/render");
    url.searchParams.set("action", "TEMPLATE");
    url.searchParams.set("text", `${type.name}, N S Physiotherapy`);
    url.searchParams.set("dates", `${iso(start)}/${iso(end)}`);
    url.searchParams.set("details", `Location: ${location === "Home Visit" ? `Home Visit, ${address}` : location}\nCondition: ${condition}\nPayment: ${payment}${payment === "Medical Aid" ? ` (${medicalAid} · ${medicalAidNumber})` : ""}\nNotes: ${notes}`);
    url.searchParams.set("location", location === "Home Visit" ? address : "N S Physiotherapy, Centurion");
    return url.toString();
  }

  function whatsappLink() {
    if (!date || !slot) return SITE.whatsapp;
    const label = `${fmt(date)} at ${slot}`;
    return `https://wa.me/${SITE.whatsappIntl}?text=${encodeURIComponent(
      `Hi, I just booked an appointment for ${label} online. Please confirm.`,
    )}`;
  }

  return (
    <PageShell>
      <PageHero eyebrow="Booking" title="Book your appointment" subtitle="No need to call, book online or WhatsApp. Choose your service, location and preferred time, then add the appointment to your Google Calendar." />
      <section className="container-page py-14">
        {Date.now() < Date.parse("2026-09-27T22:00:00Z") && (
          <div role="status" className="mx-auto mb-8 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5 text-center">
            <p className="font-semibold text-primary">No physiotherapy appointments from Thursday 24 to Sunday 27 September.</p>
            <p className="mt-1 text-sm text-muted-foreground">We'll be taking appointments again from Monday 28 September. You can still book a Monday or later slot below.</p>
          </div>
        )}
        {returnState === "checking" && (
          <div className="mx-auto max-w-lg rounded-2xl border border-border bg-card p-8 text-center shadow-sm">
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" aria-hidden />
            <h2 className="mt-5 font-display text-2xl font-bold text-primary">Confirming your payment</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Please wait while we confirm your card payment with the gateway. Do not close this page or pay again.
            </p>
          </div>
        )}

        {!confirmed && returnState !== "checking" && (
          <>
            <ol className="mb-8 grid grid-cols-4 gap-2 text-xs sm:text-sm">
              {["Service", "Location", "Date & time", "Your details"].map((l, i) => (
                <li key={l} className={`rounded-full px-3 py-2 text-center font-semibold ${step === i + 1 ? "bg-primary text-primary-foreground" : step > i + 1 ? "bg-accent text-primary" : "bg-secondary text-muted-foreground"}`}>{i + 1}. {l}</li>
              ))}
            </ol>

            {step === 1 && (
              <>
              <p className="mb-3 rounded-lg border border-border bg-accent px-3 py-2 text-sm text-foreground">{CASH_PRICE_NOTE}</p>
              <div className="grid gap-4 md:grid-cols-2">
                {TYPES.map((t) => (
                  <button key={t.id} onClick={() => { selectType(t); setStep(2); }} className={`text-left rounded-2xl border p-5 transition ${type.id === t.id ? "border-primary bg-accent shadow-[var(--shadow-elegant)]" : "border-border bg-card hover:border-primary/40"}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="font-display font-bold text-primary">{t.name}</div>
                      <div className="text-right shrink-0"><div className="text-sm font-semibold text-primary">{t.price}</div><div className="text-[11px] text-muted-foreground">cash price</div></div>
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {t.durationLabel}</div>
                  </button>
                ))}
              </div>
              </>
            )}

            {step === 2 && (
              <div className="grid gap-6 max-w-2xl">
                <div>
                  <div className="text-sm font-semibold text-primary mb-2">Where would you like to be seen?</div>
                  <div className="flex flex-wrap gap-2">
                    {LOCATIONS.map((l) => {
                      const disabled = type.id === "home" && l === "Centurion Clinic";
                      return (
                        <button key={l} disabled={disabled} onClick={() => setLocation(l)} className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm transition ${location === l ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-accent"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}><MapPin className="h-3.5 w-3.5" /> {l}</button>
                      );
                    })}
                  </div>
                </div>
                {location === "Home Visit" && (
                  <label className="grid gap-1.5 text-sm">
                    <span className="font-semibold text-primary">Your full address <span className="text-destructive">*</span></span>
                    <textarea required rows={3} value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Street address, suburb, city, gate/access instructions" className="rounded-md border border-input bg-background px-3 py-2" />
                    <span className="text-xs text-muted-foreground">Home visits cover Centurion, Midrand and surrounding areas. Please include suburb, city and any access instructions.</span>
                  </label>
                )}
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <div className="text-sm font-semibold text-primary mb-2 flex items-center gap-2"><Calendar className="h-4 w-4" /> Choose a day</div>
                  <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                    {days.map((d) => {
                      const active = date?.toDateString() === d.toDateString();
                      return (
                        <button key={d.toISOString()} onClick={() => { setDate(d); setSlot(null); }} className={`rounded-xl border p-3 text-sm transition ${active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary/40"}`}>
                          <div className="font-semibold">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
                          <div className="text-xs opacity-80">{d.toLocaleDateString(undefined, { day: "numeric", month: "short" })}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {date && (
                  <div>
                    <div className="text-sm font-semibold text-primary mb-2 flex items-center gap-2"><Clock className="h-4 w-4" /> Available times, {fmt(date)}</div>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                      {slotsForDate(date).map((s) => {
                        const taken = isSlotTaken(date, s, type.duration);
                        return (
                          <button key={s} disabled={taken} onClick={() => setSlot(s)} className={`rounded-lg border px-3 py-2 text-sm transition ${slot === s ? "border-primary bg-primary text-primary-foreground" : taken ? "border-border bg-muted text-muted-foreground line-through cursor-not-allowed opacity-60" : "border-border hover:bg-accent"}`} title={taken ? "Unavailable" : undefined}>{s}</button>
                        );
                      })}

                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 4 && (
              <form onSubmit={async (e) => {
                e.preventDefault();
                if (!date || !slot || submitting) return;
                if (!policyAccepted) {
                  setSubmitError("Please acknowledge the cancellation policy before confirming your appointment.");
                  document.getElementById("policy-accept")?.scrollIntoView({ behavior: "smooth", block: "center" });
                  return;
                }
                setSubmitting(true);
                setSubmitError(null);
                try {
                  const [h, m] = slot.split(":").map(Number);
                  const appointment = new Date(date);
                  appointment.setHours(h, m, 0, 0);
                  const res = await fetch("/api/public/bookings", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      service: type.name,
                      duration_minutes: type.duration,
                      price: type.price,
                      location,
                      address: location === "Home Visit" ? address : null,
                      appointment_at: appointment.toISOString(),
                      client_name: name,
                      email,
                      phone,
                      condition,
                      payment_method: payment,
                      medical_aid: payment === "Medical Aid" ? medicalAid : null,
                      medical_aid_number: payment === "Medical Aid" ? medicalAidNumber : null,
                      date_of_birth: (() => { const m = dob.match(/^(\d{2})\/(\d{2})\/(\d{4})$/); return m ? `${m[3]}-${m[2]}-${m[1]}` : null; })(),
                      id_type: idNumber.trim() ? idType : null,
                      id_number: idNumber.trim() || null,
                      notes: notes || null,
                      cancellation_policy_accepted_at: new Date().toISOString(),
                      cancellation_policy_version: CANCELLATION_POLICY_VERSION,
                    }),
                  });
                  let body: { error?: string; issues?: Array<{ path: (string | number)[]; message: string }> } = {};
                  try { body = await res.clone().json(); } catch { /* ignore */ }

                  if (res.status === 409) {
                    setSubmitError("Sorry, that time was just booked by someone else. Please choose a different time.");
                    setSlot(null);
                    setStep(3);
                    try {
                      const r = await fetch("/api/public/bookings");
                      if (r.ok) {
                        const b = (await r.json()) as { bookings: Array<{ appointment_at: string; duration_minutes: number }> };
                        setBookedRanges((b.bookings ?? []).map((x) => {
                          const s2 = new Date(x.appointment_at).getTime();
                          return { start: s2, end: s2 + (x.duration_minutes ?? 0) * 60 * 1000 };
                        }));
                      }
                    } catch { /* ignore */ }
                    return;
                  }
                  if (!res.ok) {
                    const fieldLabels: Record<string, string> = {
                      client_name: "Full name",
                      email: "Email",
                      phone: "Cellphone number",
                      condition: "Condition",
                      address: "Home visit address",
                      medical_aid: "Medical aid name",
                      medical_aid_number: "Medical aid number",
                      appointment_at: "Appointment time",
                      service: "Service",
                      payment_method: "Payment method",
                      location: "Location",
                    };
                    if (body.error === "invalid_input" && body.issues?.length) {
                      const msgs = body.issues.map((i) => {
                        const key = String(i.path[0] ?? "");
                        return `${fieldLabels[key] ?? key}: ${i.message}`;
                      });
                      throw new Error(`Please check the following, ${msgs.join("; ")}.`);
                    }
                    if (body.error === "invalid_json") throw new Error("We couldn't read your submission. Please refresh and try again.");
                    if (body.error === "insert_failed") throw new Error("We couldn't save your booking. Please try again, or WhatsApp 064 873 3990.");
                    if (body.error === "payments_unavailable") throw new Error("Card payments are temporarily unavailable. Please choose Cash / EFT or medical aid, or WhatsApp 064 873 3990.");
                    if (body.error === "calendar_unavailable_card") {
                      setCalendarDown(true);
                      setPayment("Cash");
                      throw new Error("We couldn't check the live diary, so card payment isn't available right now. Please choose Cash / EFT or medical aid — your booking will be provisional until the practice confirms it.");
                    }
                    if (body.error === "gateway_error") throw new Error("The card payment page could not be opened. Please try again in a moment, or choose Cash / EFT.");
                    if (body.error === "conflict_check_failed" || body.error === "fetch_failed") throw new Error("We're having trouble reaching our booking system. Please try again in a moment.");
                    throw new Error(`Booking failed (${res.status}). Please try again, or WhatsApp 064 873 3990.`);
                  }
                  const ok = body as unknown as { requiresPayment?: boolean; redirectUrl?: string; provisional?: boolean };
                  if (ok.requiresPayment && ok.redirectUrl) {
                    window.location.href = ok.redirectUrl;
                    return;
                  }
                  setProvisionalResult(Boolean(ok.provisional));
                  trackConversion("booking", "appointment_booked", {
                    service_booked: type?.name,
                    clinic_location: location,
                    value: type ? Number(String(type.price).replace(/[^0-9.]/g, "")) || undefined : undefined,
                    currency: "ZAR",
                    payment_method: payment,
                    email,
                    phone,
                    name,
                  });
                  setConfirmed(true);
                } catch (err) {
                  const msg = err instanceof Error ? err.message : "";
                  if (msg.toLowerCase().includes("failed to fetch") || msg.toLowerCase().includes("networkerror")) {
                    setSubmitError("No internet connection. Please check your network and try again.");
                  } else {
                    setSubmitError(msg || "Something went wrong. Please try again, or WhatsApp 064 873 3990.");
                  }
                } finally {
                  setSubmitting(false);
                }

              }} className="grid gap-4 max-w-2xl">
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="grid gap-1.5 text-sm">Full name<input required value={name} onChange={(e) => setName(e.target.value)} className="rounded-md border border-input bg-background px-3 py-2" /></label>
                  <label className="grid gap-1.5 text-sm">Cellphone number<input required type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="e.g. 082 123 4567" className="rounded-md border border-input bg-background px-3 py-2" /></label>
                </div>
                <label className="grid gap-1.5 text-sm">Email<input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-md border border-input bg-background px-3 py-2" /></label>
                <label className="grid gap-1.5 text-sm">Date of birth <span className="text-xs text-muted-foreground">(optional)</span><input type="text" inputMode="numeric" value={dob} onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
                  let out = digits;
                  if (digits.length > 4) out = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
                  else if (digits.length > 2) out = `${digits.slice(0, 2)}/${digits.slice(2)}`;
                  setDob(out);
                }} placeholder="DD/MM/YYYY" pattern="\d{2}/\d{2}/\d{4}" title="Enter date as DD/MM/YYYY" className="rounded-md border border-input bg-background px-3 py-2" /></label>
                <div className="grid gap-2 text-sm">
                  <span className="font-semibold text-primary">Identification <span className="text-xs font-normal text-muted-foreground">(optional)</span></span>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {(["SA ID", "Passport"] as const).map((t) => (
                      <button type="button" key={t} onClick={() => { setIdType(t); setIdNumber(""); }} className={`rounded-2xl border px-4 py-3 text-left transition ${idType === t ? "border-primary bg-accent shadow-[var(--shadow-soft)]" : "border-border hover:bg-accent/50"}`}>
                        <span className="font-semibold text-primary">{t === "SA ID" ? "South African ID" : "Passport"}</span>
                        <span className="mt-0.5 block text-xs text-muted-foreground">{t === "SA ID" ? "13 digit ID number" : "For non South African patients"}</span>
                      </button>
                    ))}
                  </div>
                  <label className="grid gap-1.5 text-sm" htmlFor="id-number">{idType === "SA ID" ? "ID number" : "Passport number"} <span className="text-xs text-muted-foreground">(optional)</span>
                    <input
                      id="id-number"
                      value={idNumber}
                      onChange={(e) => setIdNumber(idType === "SA ID" ? e.target.value.replace(/\D/g, "").slice(0, 13) : e.target.value.toUpperCase().slice(0, 20))}
                      inputMode={idType === "SA ID" ? "numeric" : "text"}
                      pattern={idType === "SA ID" ? "\\d{13}" : "[A-Za-z0-9]{5,20}"}
                      title={idType === "SA ID" ? "Enter your 13 digit South African ID number" : "Enter your passport number"}
                      placeholder={idType === "SA ID" ? "e.g. 8501015800085" : "e.g. A01234567"}
                      className="rounded-md border border-input bg-background px-3 py-2"
                    />
                  </label>
                </div>
                <label className="grid gap-1.5 text-sm">Condition / reason for visit<textarea required rows={3} value={condition} onChange={(e) => setCondition(e.target.value)} placeholder="Briefly describe what you would like help with" className="rounded-md border border-input bg-background px-3 py-2" /></label>

                <div className="grid gap-2 text-sm">
                  <span className="font-semibold text-primary">Payment method</span>
                  <div className="grid gap-2 sm:grid-cols-3">
                    {([
                      { id: "Card", label: "Pay by card now", hint: "Secure online card payment" },
                      { id: "Cash", label: "Cash / EFT", hint: "Paid on arrival at the clinic" },
                      { id: "Medical Aid", label: "Medical aid", hint: "We verify your cover" },
                    ] as const).filter((p) => !(calendarDown && p.id === "Card")).map((p) => (
                      <button type="button" key={p.id} onClick={() => setPayment(p.id)} className={`rounded-2xl border px-4 py-3 text-left transition ${payment === p.id ? "border-primary bg-accent shadow-[var(--shadow-soft)]" : "border-border hover:bg-accent/50"}`}>
                        <span className="flex items-center gap-2 font-semibold text-primary">
                          {p.id === "Card" && <CreditCard className="h-4 w-4" />} {p.label}
                        </span>
                        <span className="mt-0.5 block text-xs text-muted-foreground">{p.hint}</span>
                      </button>
                    ))}
                  </div>
                  {calendarDown && (
                    <div className="rounded-xl border border-border bg-secondary/60 px-3 py-2 text-xs text-foreground/80">
                      We couldn't check the live diary. Your booking will be provisional until the practice confirms it, so card payment isn't available right now.
                    </div>
                  )}
                  {payment === "Card" ? (
                    <div className="flex items-start gap-2 rounded-xl border border-primary/25 bg-accent/50 px-3 py-2 text-xs text-foreground/80">
                      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>You will be taken to our secure payment page to pay <strong>{type.price}</strong> by credit or debit card. Your appointment is only confirmed once payment succeeds.</span>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">Cash / EFT is paid on arrival at the clinic.</p>
                  )}
                </div>


                {payment === "Medical Aid" && (
                  <div className="grid gap-4 sm:grid-cols-2 rounded-2xl border border-border bg-secondary/50 p-4">
                    <label className="grid gap-1.5 text-sm">Medical aid scheme<input required value={medicalAid} onChange={(e) => setMedicalAid(e.target.value)} placeholder="e.g. Discovery, Bonitas" className="rounded-md border border-input bg-background px-3 py-2" /></label>
                    <label className="grid gap-1.5 text-sm">Membership number<input required value={medicalAidNumber} onChange={(e) => setMedicalAidNumber(e.target.value)} className="rounded-md border border-input bg-background px-3 py-2" /></label>
                    <p className="sm:col-span-2 text-xs text-muted-foreground">We'll verify eligibility with your medical aid before your appointment.</p>
                  </div>
                )}

                <label className="grid gap-1.5 text-sm">Additional notes (optional)<textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-md border border-input bg-background px-3 py-2" /></label>

                <div className="rounded-2xl border border-border bg-secondary/60 p-4 text-sm">
                  <div className="font-semibold text-primary">Booking summary</div>
                  <ul className="mt-2 grid gap-1 text-foreground/85">
                    <li>{type.name} · {type.durationLabel} · {type.price}</li>
                    <li>{location}{location === "Home Visit" && address ? `, ${address}` : ""}</li>
                    <li>{date ? fmt(date) : ""} at {slot ?? ""}</li>
                  </ul>
                </div>

                <CancellationPolicy />

                <label htmlFor="policy-accept" className="flex items-start gap-3 rounded-2xl border border-border bg-secondary/50 p-4 text-sm text-foreground/85">
                  <input
                    id="policy-accept"
                    type="checkbox"
                    checked={policyAccepted}
                    onChange={(e) => { setPolicyAccepted(e.target.checked); if (e.target.checked) setSubmitError(null); }}
                    className="mt-0.5 h-5 w-5 shrink-0 rounded border-input accent-[hsl(var(--primary))]"
                  />
                  <span>I have read and understand the cancellation policy and agree to the terms.</span>
                </label>


                {submitError && <div className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive">{submitError}</div>}

                <button disabled={submitting} className="btn-hero inline-flex h-12 items-center rounded-full px-6 font-semibold disabled:opacity-50">
                  {submitting ? (payment === "Card" ? "Opening secure payment..." : "Submitting...") : payment === "Card" ? `Pay ${type.price} and confirm` : "Confirm booking"}
                </button>
              </form>
            )}

            <div className="mt-8 flex justify-between">
              <button disabled={step === 1} onClick={prev} className="rounded-full border border-border px-5 py-2 text-sm font-semibold text-primary hover:bg-accent disabled:opacity-40">Back</button>
              {step > 1 && step < 4 && (
                <button onClick={next} disabled={!canContinue} className="btn-hero rounded-full px-6 py-2.5 text-sm font-semibold disabled:opacity-50">Continue</button>
              )}
            </div>
          </>
        )}

        {confirmed && (
          <div className="max-w-2xl mx-auto overflow-hidden rounded-3xl border border-border bg-card shadow-[var(--shadow-soft)]">
            <div className="bg-accent/70 px-6 py-8 text-center sm:px-8">
              <div className="booking-success-tick mx-auto grid h-20 w-20 place-items-center rounded-full bg-primary text-primary-foreground shadow-[var(--shadow-elegant)]">
                <CheckCircle2 className="h-11 w-11" />
              </div>
              <h2 className="mt-5 font-display text-2xl font-bold text-primary">{provisionalResult ? "Booking received, not yet confirmed" : "Booking confirmed"}</h2>
              <p className="mx-auto mt-2 max-w-md text-sm text-foreground/75">
                {provisionalResult
                  ? <>We couldn't check our live diary, so this booking is provisional. The practice will confirm it shortly by email to <strong>{email}</strong>.</>
                  : <>A confirmation email has been sent to <strong>{email}</strong>. Please confirm on WhatsApp.</>}
              </p>
            </div>

            <div className="m-6 grid gap-2 rounded-2xl border border-border bg-secondary/60 p-4 text-sm sm:m-8">
              <div><strong>Service:</strong> {type.name} ({type.durationLabel} · {type.price})</div>
              <div><strong>Location:</strong> {location}{location === "Home Visit" && address ? `, ${address}` : ""}</div>
              <div><strong>When:</strong> {date && fmt(date)} at {slot}</div>
              <div><strong>Payment:</strong> {paidOnline ? "Paid online by card" : payment}{payment === "Medical Aid" ? `, ${medicalAid} (${medicalAidNumber})` : ""}</div>
            </div>

            <div className="mx-6 rounded-2xl border border-primary/30 bg-accent/60 p-5 sm:mx-8">
              <div className="flex items-start gap-3">
                <Shirt className="h-5 w-5 mt-0.5 shrink-0 text-primary" />
                <div className="text-sm text-foreground/85">
                  <div className="font-semibold text-primary">Please dress for easy assessment</div>
                  <p className="mt-1">Wear clothing that allows easy examination of the affected area:</p>
                  <ul className="mt-2 list-disc pl-5 space-y-1">
                    <li><strong>Shorts</strong> for lower body conditions (knee, hip, ankle, lower back).</li>
                    <li><strong>A vest or sleeveless top</strong> for shoulder, neck or upper body assessments.</li>
                    <li>Loose, comfortable clothing you can move in.</li>
                  </ul>
                </div>
              </div>
            </div>

            <CancellationPolicy className="mx-6 mt-6 sm:mx-8" />

            <div className="flex flex-wrap justify-center gap-3 px-6 py-6 sm:px-8">
              <a href={whatsappLink()} target="_blank" rel="noreferrer" className="inline-flex h-11 items-center gap-2 rounded-full px-5 font-semibold text-white" style={{ backgroundColor: "#25D366" }}>
                <MessageCircle className="h-4 w-4" /> Confirm on WhatsApp
              </a>
              <a href={calendarLink()} target="_blank" rel="noreferrer" className="btn-hero inline-flex h-11 items-center rounded-full px-5 font-semibold">Add to Google Calendar</a>
              <button onClick={() => { setConfirmed(false); setStep(1); setDate(null); setSlot(null); }} className="inline-flex h-11 items-center rounded-full border border-border px-5 font-semibold text-primary hover:bg-accent">Book another</button>
            </div>
          </div>
        )}
      </section>
    </PageShell>
  );
}
